/**
 * useWorkspace Hook
 * 
 * Fetches workspace data and subscribes to changes.
 * Uses cache so Workspace Settings opens instantly when preloaded.
 */

import { useEffect, useState } from "react";
import type React from "react";
import { auth, db } from "../../../firebase";
import { doc, getDoc, onSnapshot } from "firebase/firestore";
import { Workspace } from "../../../types";
import { api } from "../../../services/api";
import { isDemoMode } from "../../../utils/demoMode";
import { isBypassAuthEnabled } from "../../../utils/bypassAuth";

const workspaceCache = new Map<string, { workspace: Workspace | null; isOwner: boolean; timestamp: number }>();
const CACHE_TTL_MS = 2 * 60 * 1000; // 2 min

export interface UseWorkspaceParams {
  workspaceId: string;
}

export interface UseWorkspaceResult {
  workspace: Workspace | null;
  loading: boolean;
  isOwner: boolean;
  currentUserId: string | null;
  setWorkspace: React.Dispatch<React.SetStateAction<Workspace | null>>;
  setIsOwner: React.Dispatch<React.SetStateAction<boolean>>;
}

/**
 * Hook for fetching workspace data
 */
export const useWorkspace = ({ workspaceId }: UseWorkspaceParams): UseWorkspaceResult => {
  const cached = workspaceId ? workspaceCache.get(workspaceId) : null;
  const hasValidCache = !!(cached && (Date.now() - cached.timestamp) < CACHE_TTL_MS);

  const [workspace, setWorkspace] = useState<Workspace | null>(hasValidCache && cached ? cached.workspace : null);
  const [loading, setLoading] = useState<boolean>(!hasValidCache);
  const [isOwner, setIsOwner] = useState<boolean>(hasValidCache && cached ? cached.isOwner : false);
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);

  useEffect(() => {
    // Allow loading in demo mode, bypass auth mode, or when authenticated
    const user = auth.currentUser;
    if (!user && !isDemoMode() && !isBypassAuthEnabled()) return;
    
    if (user) {
      setCurrentUserId(user.uid);
    }

    // Handle workspace not found - redirect to another workspace or landing page
    const handleWorkspaceNotFound = async (): Promise<void> => {
      // Skip redirect in demo mode or bypass auth mode
      if (isDemoMode() || isBypassAuthEnabled()) {
        return;
      }

      try {
        // Get remaining workspaces
        const remainingWorkspaces = await api.getWorkspaces();
        
        if (remainingWorkspaces.length > 0) {
          // User has other workspaces - redirect to first one
          // The backend has already updated activeWorkspaceId, so App.tsx will handle navigation
          window.location.href = "/";
        } else {
          // No other workspaces - redirect to landing/create workspace page
          window.location.href = "/create-workspace";
        }
      } catch (error) {
        // If fetching workspaces fails, fall back to home page
        console.error("Error fetching workspaces after workspace not found:", error);
        window.location.href = "/";
      }
    };

    // Fetch workspace data
    const fetchWorkspace = async (): Promise<Workspace | null> => {
      try {
        const workspaceDoc = await getDoc(doc(db, "workspaces", workspaceId));
        if (workspaceDoc.exists()) {
          const data = workspaceDoc.data();
          const workspaceData: Workspace = {
            id: workspaceDoc.id,
            name: data.name as string,
            ownerId: data.ownerId as string,
            logoUrl: data.logoUrl as string | undefined,
            createdAt: data.createdAt,
          };
          setWorkspace(workspaceData);
          // Check if user is owner (either by ownerId or by having "owner" role in members)
          if (user) {
            const isPrimaryOwner = user.uid === workspaceData.ownerId;
            // Also check if user has "owner" role in members subcollection
            let hasOwnerRole = false;
            try {
              const memberDoc = await getDoc(doc(db, "workspaces", workspaceId, "members", user.uid));
              if (memberDoc.exists()) {
                const memberData = memberDoc.data();
                hasOwnerRole = memberData?.role === "owner";
              }
            } catch (error) {
              // If we can't check members, just use primary owner check
              console.warn("Could not check member role:", error);
            }
            const owner = isPrimaryOwner || hasOwnerRole;
            if (workspaceId) workspaceCache.set(workspaceId, { workspace: workspaceData, isOwner: owner, timestamp: Date.now() });
            setIsOwner(owner);
          } else {
            setIsOwner(false);
          }
          return workspaceData;
        } else {
          // Workspace doesn't exist - redirect
          await handleWorkspaceNotFound();
          return null;
        }
      } catch (error) {
        console.error("Error fetching workspace:", error);
        // On error, try to redirect anyway
        await handleWorkspaceNotFound();
        return null;
      }
    };

    // Initial data fetch
    const initializeData = async (): Promise<void> => {
      await fetchWorkspace();
      setLoading(false);
    };

    initializeData();

    // Subscribe to workspace changes
    const unsubscribeWorkspace = onSnapshot(
      doc(db, "workspaces", workspaceId),
      async (snapshot) => {
        if (snapshot.exists()) {
          const data = snapshot.data();
          const workspaceData: Workspace = {
            id: snapshot.id,
            name: data.name as string,
            ownerId: data.ownerId as string,
            logoUrl: data.logoUrl as string | undefined,
            createdAt: data.createdAt,
          };
          const owner = user
            ? await (async () => {
                const isPrimaryOwner = user.uid === workspaceData.ownerId;
                let hasOwnerRole = false;
                try {
                  const memberDoc = await getDoc(doc(db, "workspaces", workspaceId, "members", user.uid));
                  if (memberDoc.exists()) {
                    const memberData = memberDoc.data();
                    hasOwnerRole = memberData?.role === "owner";
                  }
                } catch (e) {
                  console.warn("Could not check member role:", e);
                }
                return isPrimaryOwner || hasOwnerRole;
              })()
            : false;
          if (workspaceId) workspaceCache.set(workspaceId, { workspace: workspaceData, isOwner: !!owner, timestamp: Date.now() });
          setWorkspace(workspaceData);
          setIsOwner(!!owner);
        } else {
          // Workspace was deleted - redirect to another workspace or landing page
          setWorkspace(null);
          await handleWorkspaceNotFound();
        }
      }
    );

    return () => {
      unsubscribeWorkspace();
    };
  }, [workspaceId]);

  return {
    workspace,
    loading,
    isOwner,
    currentUserId,
    setWorkspace,
    setIsOwner,
  };
};
